'use strict';
import cart from '../cart/cart';
import { formatMessage, appendToUrl } from '../util';
import { queryAll, queryFirst, removeClass, addClass, hasClass, remove, slideUp, renderFragment } from '../domUtil';
import { trackCart } from './etmc';
import {
    SHOW_CLASS,
    ACTIVE_CLASS,
    HIDDEN_CLASS,
    SELECTED_CLASS,
    ANIM_TIME_QUICK_2
} from '../constants';
import { minitoteError } from '../templates';
import { scrollSheetTop, activateTab, resetModalSheet } from '../components/modalApi';

const $body = $('body');
const miniCartBtnContainer = queryFirst('.minicart-toggle');
const $miniCartBtnContainer = $(miniCartBtnContainer);
const miniCartSheet = queryFirst('#minicart');
const $miniCartSheet = $(miniCartSheet);
const miniCartContainer = queryFirst('.mini-cart-container', miniCartSheet);
const completeLookOpener = queryFirst('.complete-look', miniCartSheet);
const completeLookContainer = queryFirst('.complete-look-container', miniCartSheet);
const $miniCartContainer = $(miniCartContainer);
const $miniCartQuantity = $('.minicart-quantity');
const miniCartLink = queryFirst('.minicart-link', miniCartBtnContainer);
const priceElem = queryFirst('.subtotal-price', miniCartSheet);
const promoBanner = queryFirst('.promo-banner', miniCartSheet);
const mql = window.matchMedia('(min-width: 1024px)');
const ACTIVE_SHEET_PAGE_CLASS = 'active';

let isMiniCartUpdating = true;

/**
 * Updates mini cart title and close button with latest quantity.
 */
const updateTitle = () => {
    const OVERLAY_CLASS = 'title-overlay';

    const labelText = formatMessage($miniCartContainer.data('title-label'), $miniCartQuantity.text());
    const closeText = formatMessage($miniCartContainer.data('close-label'), labelText);

    const titleElem = queryFirst('.modal-header-title', miniCartSheet);
    const closeBtn = queryFirst('#minicart .close-current-modal');
    const sheetPage = queryFirst('.sheet-page', miniCartSheet);
    const miniCartContents = queryFirst('.mini-cart-contents', miniCartContainer);

    if (hasClass(miniCartContents, HIDDEN_CLASS)) addClass(sheetPage, OVERLAY_CLASS);
    else removeClass(sheetPage, OVERLAY_CLASS);

    if (titleElem) titleElem.textContent = labelText;
    if (closeBtn) closeBtn.ariaLabel = closeText;
    miniCartSheet.setAttribute('aria-label', labelText);
};

const updateMiniCartIcon = () => {
    const countVal = parseInt($miniCartQuantity.text(), 10);
    const iconToteActive = queryFirst('.icon-tote-active');
    const iconTote = queryFirst('.icon-tote');

    $miniCartQuantity.text(countVal);

    if (countVal) {
        removeClass(iconToteActive, HIDDEN_CLASS);
        addClass(iconTote, HIDDEN_CLASS);
        miniCartLink.setAttribute('aria-haspopup', 'dialog');
        miniCartLink.setAttribute('aria-expanded', false);
    } else {
        removeClass(iconTote, HIDDEN_CLASS);
        addClass(iconToteActive, HIDDEN_CLASS);
        miniCartLink.setAttribute('aria-haspopup', 'false');
        miniCartLink.removeAttribute('aria-expanded');
    }
};

/**
 * Dispatches trackItemClick + trackItemView stylitics analytics events
 * for the currently visible product panel in the mini-cart complete-look widget.
 * Used for both single and multiple recommender types to keep analytics parity.
 * @param {HTMLElement} productPanel - panel carrying data-stylitics-product-* attributes
 */
const dispatchStyliticsItemEvents = (productPanel) => {
    const styliticsCompleteLook = queryFirst('#stylitics-complete-look', miniCartSheet);
    if (!productPanel || !productPanel.dataset || !styliticsCompleteLook) return;

    const { styliticsProductId, styliticsProductItemid, styliticsProductIndex } = productPanel.dataset;
    const styliticsContainer = productPanel.closest('.js-complete-look-container');
    const { styliticsOutfitid } = styliticsContainer?.dataset || {};

    styliticsCompleteLook.dispatchEvent(
        new CustomEvent('stylitics:completeLook:trackItemClick', {
            detail: {
                pid: styliticsProductId,
                itemId: styliticsProductItemid,
                index: styliticsProductIndex,
                outfitId: styliticsOutfitid,
                placement: 'widget'
            },
            bubbles: true
        })
    );
    styliticsCompleteLook.dispatchEvent(
        new CustomEvent('stylitics:completeLook:trackItemView', {
            detail: {
                pid: styliticsProductId,
                itemId: styliticsProductItemid,
                index: styliticsProductIndex,
                outfitId: styliticsOutfitid
            },
            bubbles: true
        })
    );
};

/**
 * Updates the subtotal section in mini-cart
 */
function updateSubtotal() {
    priceElem.textContent = queryFirst('.mini-cart-contents', miniCartContainer)?.dataset.cartSubtotal || '';
}

/**
 * Displays mini cart error message
 * @param {string} message - Error message to display
 */
function createErrorNotification(message) {
    const cartError = queryFirst('.cart-error', miniCartSheet);
    if (cartError) cartError.innerHTML = minitoteError(message);
}

/**
 * Gets mini-cart sheet page elements
 * @returns {Object} mini cart page elements
 */
const getMiniCartPages = () => ({
    promoDetailsPage: queryFirst('.promo-details', miniCartSheet),
    miniCartPage: queryFirst('.minicart-page', miniCartSheet)
});

/**
 * Hides the promo page in the mini-cart sheet and show the cart page
 */
function hidePromoPage() {
    const { promoDetailsPage, miniCartPage } = getMiniCartPages();
    if (promoDetailsPage && miniCartPage) {
        removeClass(miniCartPage, HIDDEN_CLASS);
        removeClass(promoDetailsPage, ACTIVE_SHEET_PAGE_CLASS);
    }
}

/**
 * Function that gets and updates mini tote content
 * @param {boolean} isOpen - is mini tote disalog open
 */
async function showMiniToteContent(isOpen, options = {}) {
    const count = parseInt(queryFirst('.minicart-quantity', miniCartBtnContainer)?.textContent, 10);
    const url = new URL(miniCartBtnContainer.dataset.actionUrl, location.origin);

    if (isMiniCartUpdating && !miniCartSheet.getAttribute('aria-busy')) {
        const $dialog = $miniCartSheet.find('.modal-dialog');
        const $btnMultiState = $dialog.find('.js-tile-add-to-cart.m-dynamic-state:visible');

        miniCartSheet.setAttribute('aria-busy', true);

        if (isOpen) {
            $btnMultiState.removeClass('m-done').addClass('m-progress');
        } else {
            $dialog.spinner().start();
        }

        try {
            const res = await fetch(url.href);
            if (!res.ok) throw new Error('response was not OK');

            // append response data into the mini-cart container on the page
            const resData = await res.text();

            const fragment = renderFragment(resData);

            // refresh all content in minicart:
            const minicartHtml = queryFirst('.mini-cart-template', fragment);

            $miniCartContainer.empty().append(minicartHtml.content); // Needs to be jquery -- vanilla options don't eval script tags

            const promoBannerTemplate = queryFirst('.mini-cart-promoblock-template', fragment);
            promoBanner.innerHTML = '';
            promoBanner.appendChild(promoBannerTemplate.content);

            const promoDetailsTemplate = queryFirst('.mini-cart-promodetails-template', fragment);
            document.body.appendChild(promoDetailsTemplate.content);

            const emptyCartContent = queryFirst('.empty-cart-content');
            const miniCartContents = queryFirst('.mini-cart-contents', miniCartContainer);

            // if tote is empty
            if (!queryFirst('.line-item', miniCartContainer)) {
                $miniCartBtnContainer.trigger('count:update', [
                    {
                        quantityTotal: 0,
                        minicartCountOfItems: miniCartLink?.dataset.dataZeroCount
                    }
                ]);
                addClass(miniCartContents, HIDDEN_CLASS);
                removeClass(emptyCartContent, HIDDEN_CLASS);
                updateTitle();
            }

            // only update minitote recommended products when last added product is added from outside of the recommender:
            if (!isOpen) {
                const completeLookHtml = queryFirst('.complete-look-template', fragment);

                if (completeLookHtml) {
                    completeLookContainer.innerHTML = '';
                    completeLookContainer.appendChild(completeLookHtml.content);
                    $('body').trigger('minicart:tile:show', [completeLookContainer]);
                }

                if (completeLookOpener) {
                    completeLookOpener.innerHTML = '';
                    const completeLookOpenerHtml = queryFirst('.complete-look-opener-template', fragment);
                    if (completeLookOpenerHtml && completeLookOpenerHtml.content) {
                        completeLookOpener.appendChild(completeLookOpenerHtml.content);
                    }
                }
            }

            const styliticsCompleteLook = queryFirst('#stylitics-complete-look');
            if (styliticsCompleteLook) {
                // inicialize Stylitics Insights Analytics for the mini-cart
                miniCartSheet.dispatchEvent(new CustomEvent('stylitics:minicart:show'));
            }
            $miniCartContainer.closest('.modal-content').scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            isMiniCartUpdating = false;
        } catch (err) {
            console.error('Mini-cart error:', err.message);
        } finally {
            miniCartSheet.removeAttribute('aria-busy');
            if (isOpen) {
                if ($btnMultiState) {
                    $btnMultiState.removeClass('m-progress').addClass('m-done');

                    if ($btnMultiState.closest('.js-single').length) {
                        resetModalSheet($miniCartSheet);
                    }
                }
            } else {
                $dialog.spinner().stop();
            }
            // open mini-tote stylitics recommender after user logged in from the Early Access product in the recommender
            const { sessionStorage } = window;
            const openMiniTote = sessionStorage.getItem('triggerMiniTote');
            const styliticsMiniToteProductID = sessionStorage.getItem('styliticsMiniToteProductID');
            const styliticsCompleteLook = queryFirst('#stylitics-complete-look', miniCartSheet);
            if (styliticsCompleteLook && openMiniTote && styliticsMiniToteProductID) {
                const completeLookCTA = queryFirst('.complete-look-cta button', miniCartSheet);
                // trigger click on the CTA to track stylitics analytics event
                completeLookCTA.click();
                // activate tab for the last added product in the recommender if exists
                const activeTab = queryFirst(`.complete-look-thumb[data-stylitics-product-id="${styliticsMiniToteProductID}"]`);
                if (activeTab) {
                    activateTab($(activeTab));
                }
            }
            // clear session storage for the mini-tote widget
            window.sessionStorage.removeItem('triggerMiniTote');
            window.sessionStorage.removeItem('styliticsMiniToteProductID');
        }
    }

    const emptyCartContent = queryFirst('.empty-cart-content');
    if (count === 0) {
        removeClass(emptyCartContent, HIDDEN_CLASS);
        const minicart = queryFirst('.cart', miniCartSheet);
        if (minicart) {
            minicart.innerHTML = '';
        }
    } else {
        addClass(emptyCartContent, HIDDEN_CLASS);
    }

    const windowTest = w => {
        if (w.matches) {
            return;
        }
        if (hasClass(queryFirst('.main-menu'), 'in')) {
            queryFirst('.navbar-toggler')?.click();
        }
    };
    windowTest(mql);
}

/**
 * Initializes the event handlers
 */
function init() {
    cart();

    $miniCartBtnContainer.on('count:update', (event, count) => {
        if (count && Number.isFinite(parseFloat(count.quantityTotal))) {
            $miniCartBtnContainer.find('.minicart-quantity').text(count.quantityTotal);
            updateMiniCartIcon();
            updateTitle();
            updateSubtotal();
            $miniCartBtnContainer.find('.minicart-link').attr({
                'aria-label': count.minicartCountOfItems,
                title: count.minicartCountOfItems
            });
        }
    });

    $(miniCartLink).on('touchstart click', async e => {
        const { currentTarget } = e;
        const count = parseInt(queryFirst('.minicart-quantity', miniCartBtnContainer)?.textContent, 10);
        const cartContainer = queryFirst('.cart-container');

        if ((currentTarget?.dataset.sflEnabled === 'true' && !count) || cartContainer) {
            if (e) {
                e.stopImmediatePropagation();
                e.preventDefault();
            }
            window.location.href = currentTarget?.dataset.actionUrl || url;
            return;
        }
        showMiniToteContent(false);
        $('#minicart .sheet-page').removeClass(ACTIVE_CLASS);
    });

    $body.on('cart:update', () => {
        isMiniCartUpdating = true;
        updateTitle();
        updateSubtotal();
        updateMiniCartIcon();
    });

    // After a product is added to cart
    $body.on('product:afterAddToCart', (e, data) => {
        isMiniCartUpdating = true;
        updateTitle();

        const { marketingCloudAnalyticsData: mcData } = data;
        if (mcData) trackCart(mcData);

        if (!queryFirst('.cart-container') && data && !data.error) {
            // is product adding from the opened mini tote:
            const isOpen = hasClass(miniCartSheet, SHOW_CLASS);

            if (isOpen) {
                const options = {};
                if (data?.isAddedStyliticsCompleteLookItem) {
                    options.isAddedStyliticsCompleteLookItem = true;
                }

                showMiniToteContent(true, options);
            } else {
                $('.minicart-link').click();

                resetModalSheet($miniCartSheet);
            }
        }

        announceMessage('Added to your tote.');
    });

    $body.on('click', '.remove-product-btn', async e => {
        e.preventDefault();

        const { target } = e;
        const { uuid, action, name, pid, variationGroupId } = target.dataset;

        const url = appendToUrl(action, { pid, uuid, variationGroupId, minitote: true });
        const removedLineItem = target.closest('.line-item');
        const prev = removedLineItem?.previousElementSibling;

        if (prev?.matches('.mini-cart-complete-look')) {
            slideUp(prev, ANIM_TIME_QUICK_2).then(() => {
                remove(prev);
            });
        }

        slideUp(removedLineItem, ANIM_TIME_QUICK_2).then(() => {
            remove(removedLineItem);
        });

        let resData;

        try {
            const res = await fetch(url);
            resData = await res.json(); // calling json before error checking is intentional

            if (!res.ok) throw new Error('response was not OK');

            if (resData.basket.items.length) {
                const { toBeDeletedUUIDs } = resData;

                // Remove line items
                if (toBeDeletedUUIDs?.length > 0) {
                    toBeDeletedUUIDs.forEach(resUuid => {
                        const removeItem = queryFirst(`.line-item[data-product-line-item="${resUuid}"]`, miniCartContainer);
                        if (removeItem) {
                            slideUp(removeItem, ANIM_TIME_QUICK_2).then(() => {
                                remove(removeItem);
                            });
                        }
                    });
                }
            }

            // set attributes on the mini-tote nav button
            miniCartLink.setAttribute('aria-label', resData.basket.resources.minicartCountOfItems);
            miniCartLink.setAttribute('title', resData.basket.resources.minicartCountOfItems);

            // Update total + errors
            const miniCartContents = queryFirst('.mini-cart-contents', miniCartContainer);
            const { totals, valid, numItems, approachingDiscounts } = resData.basket;
            if (miniCartContents) miniCartContents.dataset.cartSubtotal = totals.subTotal;

            if (!valid.error) {
                const cartError = queryFirst('.cart-error');
                if (cartError) cartError.textContent = '';
            } else {
                createErrorNotification(valid.message);
            }

            // Show empty messaging, if tote is empty
            if (numItems === 0) {
                removeClass(queryFirst('.empty-cart-content'), HIDDEN_CLASS);
                addClass(miniCartContents, HIDDEN_CLASS);
            }

            // Replace promo banner
            promoBanner.innerHTML = resData.promoBlockHtml;

            // Update count/totals/approaching discounts
            $miniCartQuantity.empty().append(numItems);
            updateMiniCartIcon();

            $body.trigger('cart:update');
            $('.cart-container, .mini-cart-container').trigger('cart:updateTotals', resData);
            $body.trigger('removeFromMinicart', name);

            // update Marketing Cloud Analytics trackCart event
            trackCart(resData.mcAnalyticsArray);
            if (resData.updateStyliticsWidget) {
                // Show the stylitics widget for the last added product
                isMiniCartUpdating = true;
                showMiniToteContent(false);
            }

            announceMessage(`${name} was removed from your tote.`);
            $miniCartSheet.find('.close-current-modal').focus();
        } catch (err) {
            if (typeof resData === 'object') {
                const { redirectUrl, errorMessage } = resData;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }

                if (errorMessage) {
                    createErrorNotification(errorMessage);
                }
            }

            console.error('Mini-cart error:', err.message);
        }
    });

    /**
     * Shows mini-tote / hides promo details on click of promo details back and close buttons
     * event delegate -- elements won't be available on page load
     */
    $miniCartSheet.on('hidden.bs.modal', hidePromoPage);

    /**
     * Activates the sheet-page with class defined in button data-sheet attribute
     * and if the page has tabs element activates the first tab
     */
    $miniCartSheet.on('click', 'button.modal-page', (e) => {
        const { currentTarget } = e;
        const $sheet = $(currentTarget).closest('.sheet');
        const pageClass = $(currentTarget).data('page');
        const $sheetPage = $sheet.find('.sheet-page.' + pageClass);

        $sheetPage.addClass(ACTIVE_CLASS);
        requestAnimationFrame(() => scrollSheetTop($sheet));

        if ($sheetPage.find('.js-tab-controls').length) {
            activateTab($sheetPage.find('.js-tab-controls button:first-of-type'));
        } else {
            const $opener = $sheetPage.find('button.modal-back').length
                ? $sheetPage.find('button.modal-back')
                : $sheetPage.find('button.close-current-modal');
            $opener.focus();

            // Single-product recommender has no tabs, so activateTab() is not called
            // and tabActivated doesn't fire — dispatch item analytics events directly here
            const singlePanel = $sheetPage.find('.js-single.js-stylitics-product-data')[0];
            if (singlePanel) dispatchStyliticsItemEvents(singlePanel);
        }

        $sheetPage.trigger('modalContentLoaded');

        // if has color swatches, reset to the first color:
        const $firstSwatch = $sheetPage.find('.js-tile-swatch').first();

        if (!$firstSwatch.hasClass(SELECTED_CLASS)) {
            $firstSwatch.click();
        }
    });

    miniCartSheet?.addEventListener('tabActivated', e => {
        if (!e?.detail?.tabActive) return;
        dispatchStyliticsItemEvents(e.detail.tabActive);
    });

    $miniCartSheet.on('click', '.js-tile-swatch', e => {
        const $sheet = $(e.currentTarget).closest('.sheet');
        requestAnimationFrame(() => scrollSheetTop($sheet));
    });

    $miniCartSheet.on('touchend', '#stylitics-complete-look', function (e) {
        if (!window.matchMedia('(max-width: 767px)').matches) return;

        const btnSel = '.complete-look-cta > button';

        const $btn = $(this).find(btnSel).first();

        if ($(e.target).closest(btnSel).length) return;

        e.preventDefault();
        e.stopPropagation();

        $btn.click();
    });

    // open mini-tote after user logged in from the Early Access product in the recommender
    const { sessionStorage } = window;
    const styliticsMiniToteProductID = sessionStorage.getItem('styliticsMiniToteProductID');
    const openMiniTote = sessionStorage.getItem('triggerMiniTote');
    if (openMiniTote && styliticsMiniToteProductID) {
        $('.minicart-link').click();
    }
}

export default init;
